import React, { useMemo } from 'react';
import { Filter } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useAvContext } from '../../../context/AvContextProvider';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import { addCustomFiltersAsExpression, filterToExpression } from '../../../utils/filterUtils';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import { useCustomPalette } from '../../CustomDashboards/hooks';
import {
  BarCategoryWidgetTypes,
  BarLegendPosition,
  BarSubType,
  DisplayModeOptions,
  DwQueryRequest,
  Field,
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { EXCEPTION_REQUESTER_EMAIL, EXCEPTION_REQUESTER_NAME, EXCEPTION_STATUS_BUCKET_NAME, filtersFieldToIgnore } from '../ticket.types';
import { IEntityViewChartsProps } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper, getKeyValueData } from './utils';

const REQUESTER_ALIAS = 'requester_name';

const getNotEmptyFilter = (fieldName: string, condition = 'stringCondition') => ({
  and: {
    operands: [
      {
        expression: {
          fieldName,
          [condition]: {
            notEmpty: {},
          },
        },
      },
    ],
  },
});

const getActiveTicketByExceptionStatusWidget = ({ filters }: { filters: Filter }): BarCategoryWidgetTypes => ({
  id: '',
  category: WidgetCategory.Bar,
  type: BarSubType.BarVertical,
  definition: {
    title: { title: 'Active Tickets by Exception Status Buckets' },
    coords: { h: 4, w: 6, x: 0, y: 0 },
    custom: {
      customLabels: { y: 'Active Tickets' },
      rowsColumnSwitch: false,
      legend: {
        position: BarLegendPosition.Top,
        margin: { top: 20, bottom: 10 },
      },
      fieldsPalette: {},
      displayMode: DisplayModeOptions.FIT,
    },
  },
  requests: [
    {
      filter: Object.keys(filters).length
        ? {
            ...getNotEmptyFilter(EXCEPTION_STATUS_BUCKET_NAME),
            ...filters,
          }
        : getNotEmptyFilter(EXCEPTION_STATUS_BUCKET_NAME),
      select: {
        metrics: [{ name: 'active_tickets_v2_granular', alias: 'active_tickets_v2_granular' }],
        dims: [{ name: EXCEPTION_STATUS_BUCKET_NAME }],
      },
      groupBy: [EXCEPTION_STATUS_BUCKET_NAME],
      sorting: [{ name: EXCEPTION_STATUS_BUCKET_NAME, dir: SortDir.DESC }],
      projectionId: { name: 'uber_exceptions', builtIn: true },
      distinct: false,
    },
  ],
});

const getExceptionsByTicketSeverityWidget = ({ filters }: { filters: Filter }): BarCategoryWidgetTypes => ({
  id: '',
  category: WidgetCategory.Bar,
  type: BarSubType.BarVertical,
  definition: {
    title: { title: 'Exceptions by Ticket Severity' },
    coords: { h: 4, w: 6, x: 0, y: 0 },
    custom: {
      customLabels: { y: 'Exceptions' },
      rowsColumnSwitch: false,
      legend: {
        position: BarLegendPosition.Top,
        margin: { top: 20, bottom: 10 },
      },
      fieldsPalette: {},
      displayMode: DisplayModeOptions.FIT,
    },
  },
  requests: [
    {
      filter: Object.keys(filters).length
        ? {
            ...getNotEmptyFilter('ticket.severity', 'numberCondition'),
            ...filters,
          }
        : getNotEmptyFilter('ticket.severity', 'numberCondition'),
      select: {
        metrics: [{ name: 'total_exceptions', alias: 'total_exceptions' }],
        dims: [{ name: 'ticket.severity' }],
      },
      groupBy: ['ticket.severity'],
      metricsFilterThreshold: 0,
      sorting: [{ name: 'ticket.severity', dir: SortDir.ASC }],
      projectionId: { name: 'uber_exceptions', builtIn: true },
      distinct: false,
    },
  ],
});

const getExceptionsByPieWidget = ({
  title,
  filters,
  isSmallScreen,
  dims = [],
  groupBy = [],
}: {
  title: string;
  filters: Filter;
  isSmallScreen: boolean;
  dims: Field[];
  groupBy?: string[];
}): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title },
    coords: { w: 6, h: 4, x: 0, y: 4 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: Object.keys(filters).length ? filters : undefined,
      select: { dims, metrics: [{ name: 'total_exceptions', alias: 'total_exceptions' }] },
      groupBy,
      top: { size: 4, groupOthers: true },
      sorting: [{ name: 'total_exceptions', dir: SortDir.DESC }],
      projectionId: { name: 'uber_exceptions', builtIn: true },
      distinct: false,
    },
  ],
});

const ExceptionsViewCharts: React.FC<IEntityViewChartsProps> = ({ filters: f, isExpanded = true, setIsExpanded, searchFields }) => {
  const theme = useTheme();
  const customPalette = useCustomPalette([]);
  const {
    accountEntities: { fieldTypeMap },
    measurements,
  } = useAvContext();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const measurementsTypeMap = measurements.visible.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), {});

  const filters = Object.keys(f).reduce(
    (acc, field) =>
      (!filtersFieldToIgnore.includes(field) || field === 'search') && Array.isArray(f[field]) && f[field].length > 0
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    { search: [] }
  );

  const { search, ...filteredFilters } = filters;
  const queryObjectFilters = addCustomFiltersAsExpression({
    expressionFiltersObj: filterToExpression({ ...fieldTypeMap, ...measurementsTypeMap }, filteredFilters),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: search?.[0],
    fieldTypeMap,
  });

  const exceptionByTicketSeverityWidget = getExceptionsByTicketSeverityWidget({ filters: queryObjectFilters });
  const ticketsByExceptionStatusWidget = getActiveTicketByExceptionStatusWidget({ filters: queryObjectFilters });
  const exceptionsByRequesterWidget = getExceptionsByPieWidget({
    title: 'Exceptions by Requester',
    dims: [{ name: `COALESCE(${EXCEPTION_REQUESTER_NAME}, ${EXCEPTION_REQUESTER_EMAIL})`, alias: REQUESTER_ALIAS }],
    filters: queryObjectFilters,
    isSmallScreen,
    groupBy: [REQUESTER_ALIAS],
  });

  const { data: ticketsByExceptionStatus, isLoading: ticketsByExceptionStatusLoading } = useQueryObject({
    queryObject: ticketsByExceptionStatusWidget.requests.map(request => ({
      ...request,
      top: { ...request.top, size: 3 },
    })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData },
    onSuccess: data => getKeyValueData({ data, dataKey: 'active_tickets_v2_granular', titleKey: 'exception.status.status_bucket.name' }),
  });

  const { data: exceptionsByRequester, isLoading: exceptionsByRequesterLoading } = useQueryObject({
    queryObject: exceptionsByRequesterWidget.requests.map(request => ({
      ...request,
      top: { ...request.top, size: 3, groupOthers: false },
    })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData },
    onSuccess: data => getKeyValueData({ data, dataKey: 'total_exceptions', titleKey: REQUESTER_ALIAS }),
  });

  const summaryItems = useMemo(() => {
    const statuses = Object.keys(ticketsByExceptionStatus || {}) || [];
    const statusColorMap = {
      [statuses[0]]: customPalette[0],
      [statuses[1]]: customPalette[1],
      [statuses[2]]: customPalette[2],
    };
    const requesters = Object.keys(exceptionsByRequester || {}) || [];
    const requesterColorMap = {
      [requesters[0]]: customPalette[0],
      [requesters[1]]: customPalette[1],
      [requesters[2]]: customPalette[2],
    };
    return [
      {
        title: ticketsByExceptionStatusWidget.definition.title.title,
        data: ticketsByExceptionStatus,
        showPills: true,
        getPillsStyleFunc: key => ({ backgroundColor: statusColorMap[key] }),
        isLoading: ticketsByExceptionStatusLoading,
      },
      {
        title: 'Active Exceptions by Top Requesters',
        data: exceptionsByRequester,
        showPills: true,
        getPillsStyleFunc: key => ({ backgroundColor: requesterColorMap[key] }),
        isLoading: exceptionsByRequesterLoading,
        showIcon: false,
      },
    ];
  }, [ticketsByExceptionStatus, ticketsByExceptionStatusLoading, exceptionsByRequester, exceptionsByRequesterLoading, customPalette]);

  return (
    <EntityViewWidgetWrapper
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      summaryItemsProps={{ items: summaryItems, sx: { ...flex.itemsStart, gap: '70px' } }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          <Box
            sx={{
              ...entityViewWidgetStyle,
              position: 'relative',
              backgroundColor: theme => theme.palette.white.main,
            }}>
            <DraggableWidget
              widget={exceptionByTicketSeverityWidget}
              customToolbar={
                <Typography sx={{ whiteSpace: 'nowrap', mb: 1 }} variant="h6">
                  {exceptionByTicketSeverityWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: exceptionByTicketSeverityWidget })}
            </DraggableWidget>
          </Box>
          <Box
            sx={{
              ...entityViewWidgetStyle,
              position: 'relative',
              backgroundColor: theme => theme.palette.white.main,
            }}>
            <DraggableWidget
              widget={ticketsByExceptionStatusWidget}
              customToolbar={
                <Typography sx={{ whiteSpace: 'nowrap', mb: 1 }} variant="h6">
                  {ticketsByExceptionStatusWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: ticketsByExceptionStatusWidget })}
            </DraggableWidget>
          </Box>
          <Box
            sx={{
              ...entityViewWidgetStyle,
              position: 'relative',
              backgroundColor: theme => theme.palette.white.main,
            }}>
            <DraggableWidget
              widget={exceptionsByRequesterWidget}
              customToolbar={
                <Typography sx={{ whiteSpace: 'nowrap' }} variant="h6">
                  {exceptionsByRequesterWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: exceptionsByRequesterWidget })}
            </DraggableWidget>
          </Box>
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default ExceptionsViewCharts;
